/**
 * AlertSoundPrefs — a self-contained "in-app alert sound" card for a vendor's
 * Profile → Notifications screen. Lets the vendor pick their OWN order-alert tone
 * (5 presets + upload) and volume, preview them, and save. Backed by the personal
 * per-operator prefs (`/webpush/my-prefs`), the same store the "My alerts" tab and
 * the alert engine use — so a change here is what every one of that vendor's
 * dashboards will play.
 *
 * Drop-in: `<AlertSoundPrefs />` — loads, saves, and previews on its own.
 */

import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { TONES, resolveToneUrl } from "../utilities/tones";
import {
  apiGetMyNotificationPrefs,
  apiUpdateMyNotificationPrefs,
  apiUploadTone,
} from "../api/index";
import notificationSound from "../assets/img/newOrderNotification.mp3";
// How this personal choice relates to the business alerts and the staff
// team default: views/Settings/help/vendorSettingsHelp.js (vendorAlertSound).
import SettingHelp from "../views/Settings/settingsHelp";

const C = {
  wrap: { marginTop: 8 },
  head: { fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#90a0b7", marginBottom: 4 },
  sub: { fontSize: 13, color: "#6b7a99", margin: "0 0 14px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 },
  tone: (sel) => ({ display: "flex", alignItems: "center", gap: 9, padding: "9px 12px", border: `1px solid ${sel ? "#136274" : "#e6eaf0"}`, borderRadius: 10, cursor: "pointer", background: sel ? "#f2f8f7" : "#fff", margin: 0 }),
  name: { flex: 1, fontSize: 14, fontWeight: 600, color: "#334155" },
  play: { width: 28, height: 28, borderRadius: "50%", border: "none", background: "#eef2fb", color: "#0b1240", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, cursor: "pointer", flexShrink: 0 },
  row: { display: "flex", alignItems: "center", gap: 12, marginTop: 16, flexWrap: "wrap" },
  label: { fontSize: 14, fontWeight: 600, color: "#334155" },
  btn: { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 9, border: "1px solid #c7ccd3", background: "#fff", color: "#5b6b8c", cursor: "pointer" },
  save: { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, padding: "9px 18px", borderRadius: 9, border: "none", background: "#0b1240", color: "#fff", cursor: "pointer" },
  hint: { fontSize: 12, color: "#90a0b7" },
};

export default function AlertSoundPrefs() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const previewRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    apiGetMyNotificationPrefs()
      .then(({ data }) => {
        const s = data?.effective?.sound || {};
        setForm({
          tone: s.tone || "classic",
          customTone: {
            url: s.customToneUrl || data?.prefs?.customTone?.url || null,
            name: data?.prefs?.customTone?.name || (s.customToneUrl ? "Custom" : null),
          },
          volume: typeof s.volume === "number" ? s.volume : 0.6,
        });
      })
      .catch(() => setForm({ tone: "classic", customTone: { url: null, name: null }, volume: 0.6 }))
      .finally(() => setLoading(false));
  }, []);

  const selectedUrl = () => resolveToneUrl(form?.tone, form?.customTone?.url, notificationSound);
  const preview = (url, v) => {
    try {
      if (!previewRef.current) previewRef.current = new Audio();
      const a = previewRef.current;
      a.src = url;
      a.currentTime = 0;
      a.volume = Math.max(0, Math.min(1, typeof v === "number" ? v : (form?.volume ?? 0.6)));
      a.play().catch(() => {});
    } catch (_e) { /* ignore */ }
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("audio/")) { toast.error("Please choose an audio file."); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Tone is too large (max ~3MB)."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const { data } = await apiUploadTone(reader.result, file.name);
        if (data?.url) {
          setForm((f) => ({ ...f, tone: "custom", customTone: { url: data.url, name: file.name } }));
          toast.success("Custom tone uploaded. Press Save alert sound to apply it.");
        } else {
          toast.error(data?.error || "Upload failed.");
        }
      } catch (err) {
        toast.error(err?.response?.data?.error || "Upload failed.");
      }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiUpdateMyNotificationPrefs({ tone: form.tone, customTone: form.customTone, volume: form.volume });
      toast.success("Alert sound saved.");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Save failed.");
    }
    setSaving(false);
  };

  if (loading) return <div style={{ padding: "20px 0", color: "#6b7a99" }}>Loading alert sound…</div>;
  if (!form) return null;
  const vol = form.volume ?? 0.6;

  return (
    <div style={C.wrap}>
      <div style={C.head}>In-app alert sound<SettingHelp topic="vendorAlertSound" /></div>
      <p style={C.sub}>The sound your own login plays on the dashboard when a new order arrives. It changes nothing for the rest of your team. Pick one, preview it with ▶, or upload your own.</p>

      <div style={C.grid}>
        {TONES.map((t) => (
          <label key={t.id} style={C.tone(form.tone === t.id)}>
            <input type="radio" name="asp-tone" checked={form.tone === t.id}
              onChange={() => setForm((f) => ({ ...f, tone: t.id }))} style={{ margin: 0, accentColor: "#136274" }} />
            <span style={C.name}>{t.name}</span>
            <button type="button" style={C.play} aria-label={`Preview ${t.name}`}
              onClick={(e) => { e.preventDefault(); preview(t.url, vol); }}>
              <i className="fas fa-play" />
            </button>
          </label>
        ))}
        <label style={{ ...C.tone(form.tone === "custom"), opacity: form.customTone?.url ? 1 : 0.6 }}>
          <input type="radio" name="asp-tone" checked={form.tone === "custom"} disabled={!form.customTone?.url}
            onChange={() => form.customTone?.url && setForm((f) => ({ ...f, tone: "custom" }))} style={{ margin: 0, accentColor: "#136274" }} />
          <span style={C.name}>{form.customTone?.name || "Custom"}</span>
          {form.customTone?.url && (
            <button type="button" style={C.play} aria-label="Preview custom tone"
              onClick={(e) => { e.preventDefault(); preview(form.customTone.url, vol); }}>
              <i className="fas fa-play" />
            </button>
          )}
        </label>
      </div>

      <div style={C.row}>
        <span style={C.label}>Volume: {Math.round(vol * 100)}%</span>
        <input type="range" min={0} max={1} step={0.05} value={vol}
          onChange={(e) => setForm((f) => ({ ...f, volume: parseFloat(e.target.value) }))}
          style={{ flex: "1 1 180px", accentColor: "#136274" }} />
        <button type="button" style={C.btn} onClick={() => preview(selectedUrl(), vol)}>
          <i className="fas fa-volume-up" />Preview
        </button>
      </div>

      <div style={C.row}>
        <input ref={fileRef} type="file" accept="audio/*" style={{ display: "none" }} onChange={onUpload} />
        <button type="button" style={C.btn} disabled={uploading} onClick={() => fileRef.current && fileRef.current.click()}>
          <i className="fas fa-upload" />{uploading ? "Uploading…" : "Upload your own"}
        </button>
        <span style={C.hint}>MP3 or WAV, up to ~3MB.</span>
      </div>

      <div style={C.row}>
        <button type="button" style={C.save} disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save alert sound"}
        </button>
      </div>
    </div>
  );
}
